import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import tw from 'twin.macro';

import {
  SegmentCreatorData,
  useSegmentCreatorContext,
} from '@insights_module/context/SegmentCreatorContext';
import { Dropdown, IDropdownOption, Option } from '@components/Dropdown';
import { IAB_CONTEXTUAL_CATEGORIES } from '@insights_module/constants/contextualAndEntity';
import { AQLQueryCondition, AQLQueryType } from '@insights_module/adc_ql/types';
import {
  ContextualDropdownOption,
  findOptionByValue,
  getOptions,
  IabCategoriesDropdown,
} from '@insights_module/components/ContextualDropdown';
import { useFilterTypeOptions } from '@insights_module/hooks/useFilterTypeOptions';

const dropdownContextualOptions = getOptions(IAB_CONTEXTUAL_CATEGORIES, 1);

export const SegmentCreatorContextualItem = ({
  segmentData,
}: {
  segmentData: SegmentCreatorData;
}) => {
  const { condition, value } = segmentData;
  const { t } = useTranslation();
  const { pushSegmentData, isPresentational } = useSegmentCreatorContext();
  const { dropdownOptions } = useFilterTypeOptions();

  const conditionOptions: IDropdownOption[] = [
    { value: 'includes', label: <Option>{t('insights.query_builder.conditions.is')}</Option> },
    { value: 'excludes', label: <Option>{t('insights.query_builder.conditions.is_not')}</Option> },
  ];

  const [selectedType, setSelectedType] = useState<IDropdownOption>(
    dropdownOptions.find((item) => item.value === segmentData.type) || dropdownOptions[0]
  );
  const [selectedCondition, setSelectedCondition] = useState<IDropdownOption>(
    conditionOptions.find((item) => item.value === (condition as unknown as string)) ||
      conditionOptions[0]
  );
  const [selectedCategory, setSelectedCategory] = useState<ContextualDropdownOption | null>(
    findOptionByValue(dropdownContextualOptions, value as string)
  );
  const [categoryAutoFocus, setCategoryAutoFocus] = useState<boolean>(false);

  useEffect(() => {
    if (selectedType) {
      const type = selectedType.value as string;
      pushSegmentData({
        ...segmentData,
        type: type as AQLQueryType,
        category: null,
        value: null,
        condition: null,
        negation: false,
      });
    }
  }, [selectedType]);

  useEffect(() => {
    if (selectedCondition && selectedCategory) {
      const segment = {
        ...segmentData,
        condition: selectedCondition.value as unknown as Exclude<
          AQLQueryCondition,
          'includes' | 'excludes'
        >,
        value: selectedCategory.value,
      };
      pushSegmentData(segment);
    }
  }, [selectedCondition, selectedCategory]);

  useEffect(() => {
    if (!segmentData.value) {
      setCategoryAutoFocus(true);
    }
  }, []);

  return (
    <div tw='flex gap-1'>
      <Dropdown
        options={dropdownOptions}
        value={selectedType}
        onChange={(val) => setSelectedType(val as IDropdownOption)}
        labelStyle={tw`bg-spec-IAB-filter-bg border border-spec-IAB-filter-stroke text-spec-IAB-filter-text font-semibold`}
        caretStyle={tw`fill-spec-IAB-filter-text`}
        dropdownMenuStyle={tw`w-[178px]`}
        isPresentational={isPresentational}
      />

      <Dropdown
        options={conditionOptions}
        value={selectedCondition}
        onChange={(val) => setSelectedCondition(val as IDropdownOption)}
        isPresentational={isPresentational}
      />

      <IabCategoriesDropdown
        options={dropdownContextualOptions}
        autoFocus={categoryAutoFocus}
        searchPlaceholder={t('insights.query_builder.iab.search_placeholder')}
        value={selectedCategory}
        onChange={(val) => setSelectedCategory(val as ContextualDropdownOption)}
        tw='w-[200px]'
        isPresentational={isPresentational}
      />
    </div>
  );
};
